import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeLargeCtaSkeleton } from "./TypeLargeCta";
import type { TypeSmallLinkCtaSkeleton } from "./TypeSmallLinkCta";
import type { TypeTableOfContentsSettingSkeleton } from "./TypeTableOfContentsSetting";

/**
 * Fields type definition for content type 'TypeCtaSection'
 * @name TypeCtaSectionFields
 * @type {TypeCtaSectionFields}
 * @memberof TypeCtaSection
 */
export interface TypeCtaSectionFields {
    /**
     * Field type definition for field 'internalTitle' (Internal Title)
     * @name Internal Title
     * @localized false
     */
    internalTitle?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'header' (Header)
     * @name Header
     * @localized true
     */
    header?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'subHeader' (Sub Header)
     * @name Sub Header
     * @localized true
     */
    subHeader?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'mainCta' (Main CTA)
     * @name Main CTA
     * @localized true
     */
    mainCta?: EntryFieldTypes.EntryLink<TypeLargeCtaSkeleton>;
    /**
     * Field type definition for field 'secondaryCtas' (Secondary CTA's)
     * @name Secondary CTA's
     * @localized true
     */
    secondaryCtas?: EntryFieldTypes.Array<EntryFieldTypes.EntryLink<TypeSmallLinkCtaSkeleton>>;
    /**
     * Field type definition for field 'backgroundColor' (Background Color)
     * @name Background Color
     * @localized false
     */
    backgroundColor?: EntryFieldTypes.Symbol<"grey" | "light-blue" | "white">;
    /**
     * Field type definition for field 'tocSetting' (tocSetting)
     * @name tocSetting
     * @localized false
     */
    tocSetting?: EntryFieldTypes.EntryLink<TypeTableOfContentsSettingSkeleton>;
}

/**
 * Entry skeleton type definition for content type 'ctaSection' (CTA Section)
 * @name TypeCtaSectionSkeleton
 * @type {TypeCtaSectionSkeleton}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-26T23:43:09.501Z
 * @version 27
 */
export type TypeCtaSectionSkeleton = EntrySkeletonType<TypeCtaSectionFields, "ctaSection">;
/**
 * Entry type definition for content type 'ctaSection' (CTA Section)
 * @name TypeCtaSection
 * @type {TypeCtaSection}
 * @author 7BjsgI9gzpO6Dv7BH5AOT6
 * @since 2018-03-26T23:43:09.501Z
 * @version 27
 */
export type TypeCtaSection<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeCtaSectionSkeleton, Modifiers, Locales>;

export function isTypeCtaSection<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeCtaSection<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'ctaSection'
}
